import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/lib/axios';
import { axiosTasklist } from '@/utils/lib/axiosTasklist';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import useCompanyState, { ICompanyProfile, IParsedFile } from '@/states/company.state';
import { useRefetchCompletionStatus } from './useCompletionStatus';

const parseKnowledgeBaseFiles = (data: ICompanyProfile): IParsedFile[] => {
  const kb = data?.knowledge_base;
  if (typeof kb === 'string') {
    try {
      const files = JSON.parse(kb);
      if (Array.isArray(files)) {
        return files.map(f => ({
          ...f,
          name: f.filename,
          url: f.url.startsWith('http') ? f.url : `${env.apiTasklistBaseUrl}${f.url}`,
        }));
      }
    } catch (e) {
      console.error('Failed to parse knowledge base files:', e);
    }
  }
  return [];
};

export const useCompanyProfile = (tenantId: string | number | undefined) => {
  const { setCompanyProfile } = useCompanyState();

  const query = useQuery(
    ['company-profile', tenantId],
    async () => {
      if (!tenantId) throw new Error('Tenant ID is required');

      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}`;
      const { data } = await axiosInstance.get(url);

      const parsedFiles = parseKnowledgeBaseFiles(data);

      return {
        ...data,
        parsedFiles,
      } as ICompanyProfile;
    },
    {
      enabled: !!tenantId,
      retry: (failureCount, error: unknown) => {
        const err = error as { response?: { status?: number } };
        if (err?.response?.status === 404) return false;
        return failureCount < 2;
      },
    },
  );

  // Sync React Query data to Zustand store
  useEffect(() => {
    if (query.data) {
      setCompanyProfile(query.data);
    } else if (query.error) {
      const err = query.error as { response?: { status?: number } };
      if (err?.response?.status === 404) {
        setCompanyProfile(null);
      }
    }
  }, [query.data, query.error, setCompanyProfile]);

  return {
    ...query,
    isNotFound: (query.error as any)?.response?.status === 404,
  };
};

export const useCreateCompanyProfile = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({ tenantId, payload }: { tenantId: string | number; payload: Partial<ICompanyProfile> }) => {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}`;
      const { data } = await axiosInstance.post(url, payload);

      // Invalidate and refetch
      await queryClient.invalidateQueries({ queryKey: ['company-profile', tenantId] });

      // Refetch completion status
      await refetchCompletionStatus();

      toast.success('Informasi perusahaan berhasil dibuat');

      return data.data;
    },
    onError: error => {
      errorHandler(error);
    },
  });
};

export const useUpdateCompanyProfile = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({ tenantId, payload }: { tenantId: string | number; payload: Partial<ICompanyProfile> }) => {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}`;
      const { data } = await axiosInstance.put(url, payload);

      // Invalidate and refetch
      await queryClient.invalidateQueries({ queryKey: ['company-profile', tenantId] });

      // Refetch completion status
      await refetchCompletionStatus();

      toast.success('Informasi perusahaan berhasil diperbarui');

      return data;
    },
    onError: error => {
      errorHandler(error);
    },
  });
};

export const useUpdateCompanyField = () => {
  const queryClient = useQueryClient();
  const refetchCompletionStatus = useRefetchCompletionStatus();

  return useMutation({
    mutationFn: async ({
      tenantId,
      field,
      value,
      isPublished = true,
      showSuccessToast = true,
    }: {
      tenantId: string | number;
      field: string;
      value: unknown;
      isPublished?: boolean;
      showSuccessToast?: boolean;
    }) => {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}/detail`;
      const payload = {
        field,
        value,
        [`is_published_${field}`]: isPublished,
      };

      const { data } = await axiosInstance.put(url, payload);

      // Invalidate and refetch immediately
      await queryClient.invalidateQueries({ queryKey: ['company-profile', tenantId] });

      // Refetch completion status
      await refetchCompletionStatus();

      // Map field names to Indonesian
      const fieldNameMap: Record<string, string> = {
        vision: 'Visi',
        culture: 'Budaya Perusahaan',
        mission: 'Misi',
        positioning: 'Posisi Strategis',
        purpose: 'Tujuan',
        business_goal: 'Tujuan Perusahaan',
      };

      const fieldName = fieldNameMap[field] || field.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
      if (showSuccessToast) {
        toast.success(`${fieldName} berhasil diperbarui`);
      }

      return data;
    },
    onError: (error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['company-profile', variables.tenantId] });
      errorHandler(error);
    },
  });
};

// Direct upload function (not a hook) for use in Zustand stores
export const uploadCompanyMediaDirect = async (
  file: File,
  modelId: string | number,
  name = 'Company Media',
): Promise<unknown> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('model_type', 'org_profile');
  formData.append('model_id', modelId.toString());
  formData.append('collection_name', 'attachments');
  formData.append('name', name);

  const { data } = await axiosTasklist.post('/api/v1/compro/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return data;
};

export const useUploadCompanyMedia = () => {
  return useMutation({
    mutationFn: async ({
      file,
      modelId,
      name = 'Company Media',
    }: {
      file: File;
      modelId: string | number;
      name?: string;
    }) => {
      return uploadCompanyMediaDirect(file, modelId, name);
    },
    onError: error => {
      errorHandler(error);
    },
  });
};

export const useSafeUpdateCompanyField = () => {
  const createMutation = useCreateCompanyProfile();
  const updateMutation = useUpdateCompanyField();

  return useMutation({
    mutationFn: async ({
      tenantId,
      field,
      value,
      isPublished = true,
    }: {
      tenantId: string | number;
      field: string;
      value: unknown;
      isPublished?: boolean;
    }) => {
      try {
        const url = `${env.apiTasklistBaseUrl}/api/v1/compro/company-profile/${tenantId}`;
        await axiosInstance.get(url);

        return updateMutation.mutateAsync({ tenantId, field, value, isPublished });
      } catch (err: unknown) {
        const error = err as { response?: { status?: number } };
        if (error.response?.status === 404) {
          console.warn('[SAFE UPDATE] Company profile does not exist, creating...');
          const payload = { [field]: value, [`is_published_${field}`]: isPublished } as Partial<ICompanyProfile>;
          await createMutation.mutateAsync({ tenantId, payload });
          toast.success('Profil perusahaan berhasil dibuat secara otomatis!');
          return;
        }
        throw err;
      }
    },
    onError: error => {
      errorHandler(error);
    },
  });
};
